import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Flex from '@deprecated-ui/common/Flex';
import { IconWrapper } from '@deprecated-ui/common/Iconmoon';
import Link from 'next/link';

import { ORDER_TYPE } from 'constants/trading';
import Image from 'global/Image';
import tradingModalStore from 'global/TradingModal/store';
import mainSlice from 'global/MainLayout/slice';
import useTradingAccount from 'hooks/useTradingAccount';

import { selectors as profileSelectors } from 'lib/entities/profile';
import { selectors as credentialsSelectors } from 'lib/entities/credentials';
import { useNotificationData } from 'lib/entities/notification/useNotificationData';

// header components
import useSearchCompanyModalStore from 'global/Securities/stores/useSearchCompanyModalStore';
import shallow from 'zustand/shallow';
import If from '../ErrorHandlers/If';
import { Web as SearchWeb } from './Search';
import { Web as NavbarWeb } from './Navbar';
import { Web as MessageWeb } from './Message';
import { Web as NotificationWeb } from './Notification';
import { Web as ProfileMenuWeb } from './ProfileMenu';
import { Web as TradingHeaderWeb } from './InitialTrading';
import { Web as RealTradingHeader } from './RealTrading';
import { Web as VirtualTradingHeader } from './VirtualTrading';
import SearchCompanyModal from './SearchCompanyModal';

// header styled
import { HeaderWeb, HeaderWebTop, ProUser } from './styled';

const srcImage = '/img/stockbit.svg';
let lastYOffset = 0;

function Web({ isSideWidgetOpen, isHeaderMinimized, isMarketOpen }) {
  const dispatch = useDispatch();
  const { isRealTrading, isVirtualTrading, tradingType } = useTradingAccount();
  const { data, isLoading } = useSelector(profileSelectors);
  const { country = 'ID' } = data;
  const { isLoggedIn } = useSelector(credentialsSelectors);
  const [isShowSearchCompanyModal, closeSearchCompanyModal] =
    useSearchCompanyModalStore((s) => [s.visible, s.closeModal], shallow);
  const showTradingModal = tradingModalStore((s) => s.showModal);
  const getUnreadCount = useNotificationData((store) => store.getUnreadCount);

  useEffect(() => {
    const onDocumentScroll = () => {
      const currentYOffset = window.pageYOffset;
      const canScroll = document.documentElement.scrollHeight > window.innerHeight;
      if (!canScroll) {
        dispatch(mainSlice.actions.setHeaderMinimized(false));
      }
      if (currentYOffset < 150) return;
      if (currentYOffset > lastYOffset && !isHeaderMinimized) {
        // down
        dispatch(mainSlice.actions.setHeaderMinimized(true));
      } else if (currentYOffset < lastYOffset && isHeaderMinimized) {
        // up
        dispatch(mainSlice.actions.setHeaderMinimized(false));
      }
      lastYOffset = currentYOffset;
    };

    window.addEventListener('scroll', onDocumentScroll);
    // Clean eventlistener when unmount component
    return () => {
      window.removeEventListener('scroll', onDocumentScroll);
    };
  }, [dispatch, isHeaderMinimized]);

  useEffect(() => {
    if (isLoggedIn) {
      getUnreadCount();
    }
  }, []);

  useEffect(
    () => () => {
      dispatch(mainSlice.actions.setHeaderMinimized(false));
    },
    [],
  );

  const renderTradingPopup = () => {
    if (isRealTrading) {
      return <RealTradingHeader minimize={isHeaderMinimized} />;
    }
    if (isVirtualTrading) {
      return <VirtualTradingHeader minimize={isHeaderMinimized} />;
    }
    if (!tradingType) {
      return <TradingHeaderWeb minimize={isHeaderMinimized} />;
    }
    return <div />;
  };

  const onSymbolChange = (newSymbol) => {
    closeSearchCompanyModal();

    showTradingModal({
      symbol: newSymbol,
      orderType: isRealTrading ? ORDER_TYPE.BUY : ORDER_TYPE.VIRTUAL_BUY,
      modalData: {
        source: 'Other',
      },
    });
  };

  return (
    <HeaderWeb
      id="stockbit-header-web"
      isOpen={isSideWidgetOpen}
      isMarketOpen={isMarketOpen}
    >
      <div style={{ width: '100%' }}>
        <HeaderWebTop>
          <Flex align="center">
            <Link href="/stream">
              <a>
                <IconWrapper>
                  <Image
                    alt=""
                    src={srcImage}
                    style={{
                      height: '40px',
                      marginRight: '30px',
                      width: '75px',
                    }}
                  />
                </IconWrapper>
              </a>
            </Link>
            <SearchWeb style={{ padding: '0 30px' }} />
          </Flex>
          <Flex align="center">
            {!isLoading && !data.ispro ? (
              <Link href="/shop">
                <ProUser>Berlangganan Pro</ProUser>
              </Link>
            ) : null}
            <MessageWeb />
            <NotificationWeb />
            <ProfileMenuWeb />
          </Flex>
        </HeaderWebTop>
        <NavbarWeb minimize={isHeaderMinimized} />
      </div>
      <If condition={country === 'ID'}>{renderTradingPopup()}</If>
      {isShowSearchCompanyModal && (
        <SearchCompanyModal
          onSymbolChange={(symbol) => onSymbolChange(symbol)}
        />
      )}
    </HeaderWeb>
  );
}

Web.propTypes = {
  isSideWidgetOpen: PropTypes.bool,
  isHeaderMinimized: PropTypes.bool,
  isMarketOpen: PropTypes.bool,
};

Web.defaultProps = {
  isSideWidgetOpen: false,
  isHeaderMinimized: false,
  isMarketOpen: false,
};

export default Web;
